import isObject from 'lodash/isObject'
import isNumber from 'lodash/isNumber'
import Hammer from 'hammerjs'
import Util from '@antv/f2/lib/util/common'
import Helpers from '@antv/f2/lib/util/helper'

const THRESHOLD_TIME = 100

export default class ElasticController {
  // 自定义配置
  mode = 'x' // 'x', 'y', 'xy'
  scaleMax = null // 自动计算/自定义
  clipMargin = [10, 0, 0, 0] // 上右下左
  _autoScaleMax = null
  // 缩放
  _scale = 1
  // 偏移
  _translateX = 0 // 正数
  _translateY = 0 // 负数
  // 是否启用
  _enable = false
  // canvas & canvas dom
  el = null
  canvas = null
  // origin convertPoint
  convertPoint = null
  // hammer
  _labelShowCache = {}
  /**
   *
   * @type {Manager}
   */
  hammer = null

  // state
  _timestamp = 0
  _pinching = false
  _panning = false
  _pressed = false
  _currentDeltaX = null
  _currentDeltaY = null
  _currentPinchScaling = null
  // 选中的 tooltip
  _tooltipItems = null
  // 初始化值
  initSelectItem = null

  _action = null // pan/pinch/tap

  selectStyle = {
    fillOpacity: 1
  }
  unSelectStyle = {
    fillOpacity: 0.8
  }

  constructor(chart) {
    this.chart = chart

    const Canvas = chart.get('canvas')
    this.el = Canvas && Canvas.get('el')
    this.canvas = Canvas
  }

  isHorizontal() {
    return this.mode.includes('x')
  }

  isVertical() {
    return this.mode.includes('y')
  }

  isEnable() {
    return this._enable
  }

  /**
   * 获取缩放
   * @return {Number[]}
   */
  getScale() {
    if (!this.isEnable()) return [1, 1]

    if (this.isHorizontal() && this.isVertical()) {
      return [this._scale, this._scale]
    }
    if (this.isVertical()) {
      return [1, this._scale]
    }
    if (this.isHorizontal()) {
      return [this._scale, 1]
    }
    return [1, 1]
  }

  /**
   * 获取偏移
   * @return {Number[]}
   */
  getTranslate() {
    if (!this.isEnable()) return [0, 0]

    if (this.isHorizontal() && this.isVertical()) {
      return [this._translateX, this._translateY]
    }
    if (this.isVertical()) {
      return [0, this._translateY]
    }
    if (this.isHorizontal()) {
      return [this._translateX, 0]
    }
    return [0, 0]
  }

  /**
   * 获取缩放模式
   * @return {string}
   */
  getMode() {
    return this.mode
  }

  enable(cfg) {
    this._enable = true

    if (!isObject(cfg)) {
      this.bindEvents()
      return
    }
    // 自定义配置
    const { mode, scaleMax, initSelectItem, clipMargin } = cfg
    if  (['x', 'y', 'xy'].includes(mode)) {
      this.mode = mode
    }

    if (isNumber(scaleMax)) {
      // 不支持缩小
      this.scaleMax = Math.max(1, scaleMax)
    }

    if (isNumber(clipMargin)) {
      this.clipMargin = [clipMargin, clipMargin, clipMargin, clipMargin]
    }

    if (Array.isArray(clipMargin) && clipMargin.length > 0 && clipMargin.every(d => isNumber(d))) {
      const [top, right, bottom, left] = clipMargin
      this.clipMargin = [
        top,
        right === undefined ? top : right,
        bottom === undefined ? top : bottom,
        left === undefined ? (right === undefined ? top : right) : left
      ]
    }

    this.initSelectItem = initSelectItem
    this.bindEvents()
  }

  /**
   * 绑定交互事件
   */
  bindEvents() {
    this.unbindEvents()
    const { el } = this
    if (!el) return

    const { Tap, Pan, Pinch, DIRECTION_HORIZONTAL, DIRECTION_VERTICAL, DIRECTION_ALL } = Hammer

    let direction
    if (this.isHorizontal() && this.isVertical()) {
      direction = DIRECTION_ALL
    } else if (this.isVertical()) {
      direction = DIRECTION_VERTICAL
    } else if (this.isHorizontal()) {
      direction = DIRECTION_HORIZONTAL
    }

    this.hammer = new Hammer(el, {
      recognizers: [
        [Pinch, { enable: true }],
        [Pan, { direction }, null, 'pinch'],
        [Tap]
      ],
    })

    this.hammer.on('panstart', this.panStart)
    this.hammer.on('panmove',  this.panMove)
    this.hammer.on('panend',  this.panEnd)
    this.hammer.on('pinchstart',  this.pinchStart)
    this.hammer.on('pinch', this.pinch)
    this.hammer.on('pinchend', this.pinchEnd)
    this.hammer.on('touchend', this.reset)
    this.hammer.on('tap', this.handleTap)

    this.bindWheel(el)
  }
  // 绑定鼠标滚动事件
  bindWheel(el) {
    el.addEventListener('wheel', this.onWheel)
  }

  unbindWheel() {
    const { el } = this
    if (!el) return
    el.removeEventListener('wheel', this.onWheel)
  }

  /**
   * 取消事件绑定
   */
  unbindEvents() {
    if (this.hammer) {
      this.hammer.destroy()
      this.hammer = null
    }
    this.unbindWheel()
  }

  /**
   * 销毁
   */
  destroy() {
    this.unbindEvents()
    this.el = null
    this.canvas = null
    this._labelShowCache = null
    this._tooltipItems = null
    this.initSelectItem = null
  }

  /**
   * 判断开始事件是否在画布中
   * @param e
   * @return {*}
   */
  checkActionInCoord(e) {
    const { chart } = this
    e.clientX = e.center.x
    e.clientY = e.center.y
    const { x, y } = Util.createEvent(e, chart)
    const plot = this.chart.get('plotRange')
    const inPlot = Helpers.isPointInPlot({ x, y }, plot)
    if (!inPlot) {
      this._pressed = false
      this._pinching = false
      this._panning = false
    }
    return inPlot
  }

  /**
   * pan start
   * @param e
   */
  panStart = (e) => {
    if (this._pressed || this._pinching || this._panning) return
    if (!this.checkActionInCoord(e)) return

    this._panning = true

    this._currentDeltaX = 0
    this._currentDeltaY = 0

    this.handleMove(e)
  }

  /**
   * pan moving
   */
  panMove = (e) => {
    if (this._pressed || this._pinching) return
    if (!this._panning) return

    this.handleMove(e)
  }

  /**
   * pan end
   */
  panEnd = () => {
    if (this._pressed || this._pinching) return

    if (!this._panning) return

    this._currentDeltaX = null
    this._currentDeltaY = null

    setTimeout(() => {
      this._panning = false
    }, THRESHOLD_TIME)
  }

  handleMove = (e) => {
    const { _scale, _panning } = this
    if (!_panning) return

    if (_scale === 1) {
      this.handleShowTooltip(e)
      return
    }

    if (e.type === 'swipe' && e.deltaTime > 350) {
      // 区分 pan 操作和 swipe 操作
      return
    }
    const { _currentDeltaY, _currentDeltaX } = this
    let deltaX
    let deltaY
    if (_currentDeltaX !== null && _currentDeltaY !== null) {
      const gain = Math.sqrt(_scale)
      deltaX = (e.deltaX * gain) - _currentDeltaX
      deltaY = (e.deltaY * gain) - _currentDeltaY
      this._currentDeltaX = e.deltaX * gain
      this._currentDeltaY = e.deltaY * gain
    }

    if (Math.abs(deltaX) > 0 || Math.abs(deltaY) > 0) {
      const lastTimestamp = this._timestamp
      const now = +new Date()

      if (now - lastTimestamp > 16) {
        this.doMove(deltaX, deltaY)

        this._timestamp = now
      }
    }
  }

  doMove(deltaX, deltaY) {
    let { _translateY: translateY, _translateX: translateX } = this

    if (this.isHorizontal() && deltaX !== 0) {
      translateX += deltaX
    }

    if (this.isVertical() && deltaY !== 0) {
      translateY += deltaY
    }

    const [checkedTranslateX, checkedTranslateY] = this.checkTranslateBound(translateX, translateY)

    this._translateX = checkedTranslateX
    this._translateY = checkedTranslateY
    this.repaint('pan')
  }

  /**
   * 计算偏移是否超出边界并返回修正后的偏移
   * @param x
   * @param y
   */
  checkTranslateBound(x, y) {
    const { chart, _scale } = this
    const { start, end, transposed } = chart.get('coord')

    const width = Math.abs(end.x - start.x)
    const height = Math.abs(end.y - start.y)

    const availableTranslateX = this.isHorizontal() ? (1 - _scale) * width : 0
    const availableTranslateY = this.isVertical() ? (_scale - 1) * height : 0

    if (transposed) {
      return [Math.min(Math.max(x, availableTranslateX), 0), Math.min(Math.max(y, -availableTranslateY), 0)]
    }
    return [Math.min(Math.max(x, availableTranslateX), 0), Math.max(Math.min(y, availableTranslateY), 0)]
  }

  pinchStart = (e) => {
    if (this._pressed || this._panning || this._pinching) return
    if (!this.checkActionInCoord(e)) return

    this._currentPinchScaling = 1
    this._pinching = true
    const { chart } = this

    const coord = chart.get('coord')
    this.pinchPoint = coord.invertPoint(Util.createEvent(e, chart))
  }

  pinch = (e) => {
    if (this._pressed || this._panning) return
    if (!this._pinching) return
    this.handlePinch(e)
  }

  pinchEnd = (e) => {
    if (!this._pinching) return
    this.handlePinch(e)
    this._currentPinchScaling = null
    setTimeout(() => {
      this._pinching = false
    }, THRESHOLD_TIME)
  }

  handlePinch(e) {
    const { _currentPinchScaling } = this
    const diff = (1 / _currentPinchScaling * e.scale) ** 1.618
    
    const x = Math.abs(e.pointers[0].clientX - e.pointers[1].clientX)
    const y = Math.abs(e.pointers[0].clientY - e.pointers[1].clientY) // diagonal fingers will change both (xy) axes

    const p = x / y
    let xy

    if ((p > 0.3 && p < 1.7) || this.mode === 'xy') {
      xy = 'xy'
    } else if (x > y) {
      xy = 'x'
    } else {
      xy = 'y'
    }

    const lastTimestamp = this._timestamp
    const now = +new Date()

    if (now - lastTimestamp > 16) {
      this.doZoom(diff, xy)

      this._timestamp = now
    } // Keep track of overall scale

    this._currentPinchScaling = e.scale

  }

  // 鼠标滚轮事件
  onWheel = (e) => {
    const { chart } = this
    const coord = chart.get('coord')
    this.pinchPoint = coord.invertPoint({
      type: 'wheel',
      chart,
      native: e || null,
      x: e.offsetX,
      y: e.offsetY
    })
    let diff = 1
    if (e.deltaY > 0) {
      diff = 1.1
    } else {
      diff = 0.9
    }
    this.doZoom(diff, this.mode)
  }

  checkScaleBound(scale) {
    const { scaleMax: customScaleMax, chart, _autoScaleMax: autoScaleMax } = this

    if (isNumber(customScaleMax)) {
      return Math.min(customScaleMax, Math.max(scale, 1))
    }

    if (isNumber(autoScaleMax)) {
      return Math.min(autoScaleMax, Math.max(scale, 1))
    }

    const geoms = chart.get('geoms')
    let scaleMax = 5
    if (Array.isArray(geoms) && geoms.length > 0) {
      const dataArray = geoms[0].get('dataArray')
      if (Array.isArray(dataArray) && Array.isArray(dataArray[0])) {
        scaleMax = Math.floor(dataArray[0].length / 5)
      }
    }

    scaleMax = Math.max(5, scaleMax)
    this._autoScaleMax = scaleMax
    return Math.min(scaleMax, Math.max(scale, 1))
  }

  doZoom(diff, whichAxes) {
    if (this.isHorizontal() && !this.isVertical() && !whichAxes.includes('x')) return
    if (this.isVertical() && !this.isHorizontal() && !whichAxes.includes('y')) return
    let { _scale: scale } = this
    const { chart } = this
    const originScale = scale
    scale *= diff

    this._scale = this.checkScaleBound(scale)

    // reset
    if (this._scale === 1) {
      this._translateY = 0
      this._translateX = 0
      this.repaint('pinch')
      return
    }

    const coord = chart.get('coord')

    const { x: xDelta, y: yDelta } = this.handleTranslateOffset(originScale, this._scale, this.pinchPoint)

    if (this.isHorizontal() && whichAxes.includes('x')) {
      // x 轴偏移
      this._translateX = this.checkTranslateBound(this._translateX - xDelta, this._translateY)[0]
    }

    if (this.isVertical() && whichAxes.includes('y')) {
      // y 轴偏移
      this._translateY = this.checkTranslateBound(this._translateX, coord.transposed ? this._translateY - yDelta : this._translateY + yDelta)[1]
    }

    this.repaint('pinch')
  }

  handleTranslateOffset(originScale, scale, point) {
    const { chart } = this
    const coord = chart.get('coord')
    const { start, end } = coord
    const width = Math.abs(end.x - start.x)
    const height = Math.abs(end.y - start.y)

    const originDelta = {
      x: (originScale - 1) * width,
      y: (originScale - 1) * height
    }

    const delta = {
      x: (scale - 1) * width,
      y: (scale - 1) * height,
    }

    const xValue = coord.transposed ? point.y : point.x
    const yValue = coord.transposed ? (1 - point.x) : point.y

    return {
      x: (delta.x - originDelta.x) * xValue,
      y: (delta.y - originDelta.y) * yValue
    }
  }

  /**
   * 重置缩放状态，用于横竖屏切换
   */
  reset = () => {
    this._scale = 1
    this._translateX = 0
    this._translateY = 0
    this._pinching = false
    this._panning = false
    this._pressed = false
    this._currentDeltaX = null
    this._currentDeltaY = null
    this._currentPinchScaling = null
  }

  handleTap = (e) => {
    if (this._pressed || this._panning || this._pinching) return

    this._pressed = true
    if (!this.checkActionInCoord(e)) return

    this.handleShowTooltip(e)

    setTimeout(() => {
      this._pressed = false
    }, THRESHOLD_TIME)
  }

  handleShowTooltip(e) {
    const { chart } = this
    if (chart && e) {
      if (['panstart', 'panmove'].includes(e.type)) {
        e.clientX = e.center && e.center.x
        e.clientY = e.center && e.center.y
      }
      const point = Util.createEvent(e, chart)

      this.handleHighlightShape(point)
      chart.showTooltip(point)
    }
  }

  repaint(action) {
    const { chart } = this
    if (!chart) return

    const animate = chart.get('animate')
    chart.animate({
      'axis-tick': false,
      'axis-grid': false,
      'axis-label': false,
    })
    this._action = action

    chart.repaint()

    chart.animate(animate)
  }

  saveTooltipActive(activeItems) {
    if (Array.isArray(activeItems) && activeItems.length > 0) {
      this._tooltipItems = activeItems
    }
  }

  getTooltipActiveItems() {
    return this._tooltipItems
  }

  getTooltipInitItem() {
    return this.initSelectItem
  }

  /**
   * 获取所有 interval shapes
   * @return {[]}
   */
  getIntervalShapes() {
    let children = []
    const { chart } = this
    const geoms = chart.get('geoms')
    geoms.forEach((geom) => {
      if (geom.get('type') === 'interval') {
        const container = geom.get('container')
        children = children.concat(container.get('children'))
      }
    })

    return children
  }

  handleHighlightShape(e) {
    const { chart } = this

    const shapes = this.getIntervalShapes()

    const geoms = chart.get('geoms')

    const snaps = geoms.map((geom) => {
      if (!geom.get('visible')) return []
      const type = geom.get('type')
      if (type === 'interval') {
        return geom.getSnapRecords(e)
      } else {
        return []
      }
    }).reduce((acc, items) => [...acc, ...items], [])

    shapes.forEach((shape) => {
      if (shape.get('isShape') && (shape.get('className') === 'interval')) {
        const shapeData = shape.get('origin')._origin
        if (snaps.find(d => d._origin === shapeData)) {
          this.setSelectShapes(shape, true)
        } else {
          this.setSelectShapes(shape)
        }
      }
    })
  }

  setSelectShapes(shape, selected = false) {
    const { selectStyle, unSelectStyle } = this

    if (selected) {
      if (!shape.get('_originAttrs')) {
        const originAttrs = { ...shape.attr() }
        shape.set('_originAttrs', originAttrs)
      }

      shape.attr(selectStyle)
      shape.set('_selected', true)
      return
    }

    if (!shape.get('_originAttrs')) {
      const originAttrs = { ...shape.attr() }
      shape.set('_originAttrs', originAttrs)
    } else {
      shape.attr(shape.get('_originAttrs'))
    }
    shape.set('_selected', false)
    unSelectStyle && shape.attr(unSelectStyle)
  }

  getAction() {
    const action = this._action
    this._action = null // 初始化为 null 区分第一次以及是否需要重新计算 label 状态
    return action
  }

  getCaches() {
    return this._labelShowCache
  }

  getClipMargin() {
    return this.clipMargin
  }
}

